/* Sportiff Performance: kit configurator (sections/sp-kit-configurator.liquid) */
(() => {
  if (customElements.get('sp-kitcfg')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (c) => (window.SP?.utils?.formatMoney ? window.SP.utils.formatMoney(c) : `₹${Math.round(c / 100)}`);
  const pad = (n) => String(n).padStart(2, '0');
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  const ICON_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 5v14M5 12h14"/></svg>';

  class SpKitCfg extends HTMLElement {
    connectedCallback() {
      try { this.slots = JSON.parse(this.querySelector('[data-kc-data]').textContent); } catch (e) { return; }
      this.rows = [];
      this.uid = 0;
      this.view = this.querySelector('[data-kc-view]');
      this.deck = this.querySelector('[data-kc-deck]');
      this.viewHome = this.querySelector('[data-kc-view-slot]');
      this.dlg = this.querySelector('[data-kc-dlg]');
      // multi-buy offer (Theme settings → Cricket Whites offer); the discount itself is applied in the cart and at checkout
      const pct = Number(this.dataset.offerPct);
      this.offer = pct > 0 ? { pct, min: Number(this.dataset.offerMin) || 2, name: this.dataset.offerName || 'Offer' } : null;
      this.slides = [];
      this.vi = 0;
      this.pos = 0;
      this.target = 0;
      this.vel = 0;
      this.vKey = '';
      // the kit's style comes from the first slot's pre-selected piece; every slot starts in that style
      const lead = this.slots.find((s) => s.products.length);
      this.kitStyle = lead ? this.defaultOf(lead).style : null;
      this.slots.forEach((slot, si) => {
        if (!slot.products.length) return;
        this.rows.push(this.newRow(si, this.pickFor(slot, this.kitStyle, null), false));
      });
      this.querySelectorAll('[data-kc-add]').forEach((b) => b.addEventListener('click', () => this.addAll()));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('pointerover', (e) => this.hot(e.target.closest('[data-row]')?.dataset.row));
      this.addEventListener('pointerleave', () => this.hot(null));
      if (this.deck) {
        this.deck.addEventListener('click', (e) => this.onShot(e));
        this.initGestures();
        if ('ResizeObserver' in window) new ResizeObserver(() => this.measure()).observe(this.deck);
      }
      if (this.dlg) {
        this.dlg.addEventListener('cancel', (e) => { e.preventDefault(); this.closeFull(); });
        document.addEventListener('fullscreenchange', () => { if (!document.fullscreenElement && this.dlg.open) this.closeFull(); });
      }
      this.render();
      this.ready = true;
    }

    newRow(si, product, extra) {
      return { id: `r${++this.uid}`, slot: si, product, variant: null, qty: 1, on: true, extra };
    }

    /* ---------- helpers ---------- */
    // every style across the kit, in slot order; the thumb is the first slot's piece in that style
    kitStyles() {
      const map = new Map();
      this.slots.forEach((s) => s.products.forEach((p) => { if (!map.has(p.style)) map.set(p.style, { style: p.style, thumb: p.thumb, available: false }); }));
      this.slots.forEach((s) => s.products.forEach((p) => { if (p.available) map.get(p.style).available = true; }));
      return [...map.values()];
    }
    cutsOf(slot, style) { return slot.products.filter((p) => p.style === style && p.cut); }
    defaultOf(slot) { return slot.products.find((p) => p.handle === slot.defaultHandle && p.available) || slot.products.find((p) => p.available) || slot.products[0]; }
    // the slot's piece in `style`: in stock first, keeping the current cut (or the slot default on first load).
    // A slot with nothing in that style (e.g. extras) keeps what it has.
    pickFor(slot, style, current) {
      const pool = slot.products.filter((p) => p.style === style);
      if (!pool.length) return current || this.defaultOf(slot);
      const live = pool.filter((p) => p.available);
      const from = live.length ? live : pool;
      return from.find((p) => (current ? p.cut && p.cut === current.cut : p.handle === slot.defaultHandle)) || from[0];
    }
    // what a photo shows, in words: "Shirt", "Trouser", "Shirt · extra"
    pieceLabel(r) { return `${this.slots[r.slot].label}${r.extra ? ' · extra' : ''}`; }
    imagesOf(p) { return p.images?.length ? p.images : [{ src: p.img, srcset: '', thumb: p.thumb, ratio: 0.75, alt: p.name }]; }
    rowById(id) { return this.rows.find((r) => r.id === id); }
    active() { return this.rows.filter((r) => r.on); }

    /* ---------- rendering ---------- */
    render() {
      this.slots.forEach((slot, si) => {
        const host = this.querySelector(`[data-kc-slot="${si}"]`);
        if (!host) return;
        const rows = this.rows.filter((r) => r.slot === si);
        host.innerHTML = rows.map((r) => this.rowHTML(r)).join('') +
          (slot.repeatable ? `<button type="button" class="kc-add-more" data-kc-more="${si}">${ICON_PLUS}<span>${esc(slot.addLabel || 'Add another')}</span></button>` : '');
      });
      this.renderStyles();
      this.renumber();
      this.renderViewer();
      this.renderTotals();
    }

    renderStyles() {
      const host = this.querySelector('[data-kc-styles]');
      if (!host) return;
      const styles = this.kitStyles();
      host.hidden = styles.length < 2;
      if (host.hidden) return;
      // "Shirt and trouser switch together": the slots the style actually changes
      const labels = this.slots.filter((s) => new Set(s.products.map((p) => p.style)).size > 1).map((s, i) => (i ? s.label.toLowerCase() : s.label));
      const note = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]} switch together` : '';
      host.innerHTML = `
        <div class="kc-style__head"><span class="t-label t-muted" id="${this.id || 'kc'}-style">Style</span>${note ? `<span class="t-small t-muted">${esc(note)}</span>` : ''}</div>
        <div class="kc-style__opts" role="group" aria-labelledby="${this.id || 'kc'}-style">${styles.map((s) =>
          `<button type="button" class="kc-style__opt" data-kc-style="${esc(s.style)}" aria-pressed="${s.style === this.kitStyle}" ${s.available ? '' : 'disabled'}><img src="${s.thumb}" alt="" loading="lazy"><span>${esc(s.style)}</span></button>`).join('')}</div>`;
    }

    rowHTML(r) {
      const slot = this.slots[r.slot];
      const p = r.product;
      const cuts = this.cutsOf(slot, p.style);
      const cutLabel = cuts.some((c) => /sleeve/i.test(c.cut)) ? 'Sleeve' : 'Cut';
      const price = (r.variant?.price ?? p.price);
      const chip = (attrs, label, pressed, disabled) =>
        `<button type="button" class="kc-chip" ${attrs} aria-pressed="${pressed}" ${disabled ? 'disabled' : ''}>${esc(label)}</button>`;
      const control = slot.optional && !r.extra
        ? `<button type="button" class="kc-switch" role="switch" aria-checked="${r.on}" aria-label="Include ${esc(slot.label)}" data-kc-toggle="${r.id}"></button>`
        : r.extra ? `<button type="button" class="kc-icon-btn" aria-label="Remove" data-kc-remove="${r.id}">${ICON_X}</button>` : '';
      return `
        <div class="kc-row${r.on ? '' : ' is-off'}${r.isNew ? ' is-new' : ''}" data-row="${r.id}">
          <div class="kc-row__head">
            <button type="button" class="kc-thumb" data-kc-look="${r.id}" aria-label="See photos of ${esc(p.name)}"><img src="${p.thumb}" alt="" loading="lazy"><span class="kc-num" data-kc-num></span></button>
            <div class="kc-row__title"><span class="t-label t-muted">${esc(r.extra ? `${slot.label} · extra` : slot.label)}</span><a class="kc-row__name" href="${p.url}">${esc(p.name)}</a></div>
            <span class="kc-row__price t-num">${money(price * r.qty)}${p.compare > p.price ? `<s>${money(p.compare * r.qty)}</s>` : ''}</span>
            ${control}
          </div>
          <div class="kc-row__opts">
            ${cuts.length > 1 ? `<div class="kc-pick"><span class="kc-pick__label t-label t-muted">${cutLabel}</span><div class="kc-chips" role="group" aria-label="${cutLabel}">${cuts.map((c) => chip(`data-kc-cut="${esc(c.handle)}" data-for="${r.id}"`, c.cut, c.handle === p.handle, !c.available)).join('')}</div></div>` : ''}
            <div class="kc-pick"><span class="kc-pick__label t-label t-muted">Size</span><div class="kc-chips" role="group" aria-label="Size and quantity">${p.variants.map((v) => `<button type="button" class="kc-chip kc-size" data-kc-size="${v.id}" data-for="${r.id}" aria-pressed="${r.variant?.id === v.id}" ${v.available ? '' : 'disabled'}>${esc(v.size)}</button>`).join('')}<div class="kc-qty"><button type="button" data-kc-qty="-1" data-for="${r.id}" aria-label="Decrease quantity" ${r.qty <= 1 ? 'disabled' : ''}>−</button><output aria-label="Quantity">${r.qty}</output><button type="button" data-kc-qty="1" data-for="${r.id}" aria-label="Increase quantity" ${r.qty >= 10 ? 'disabled' : ''}>+</button></div></div></div>
          </div>
        </div>`;
    }

    renumber() {
      let n = 0;
      this.rows.forEach((r) => {
        const el = this.querySelector(`[data-row="${r.id}"] [data-kc-num]`);
        if (r.on) n += 1;
        if (el) el.textContent = r.on ? pad(n) : '–';
        r.n = r.on ? n : null;
        r.isNew = false;
      });
    }

    /* ---------- viewer: up to 3 photos of every chosen piece, dealt as a deck ----------
       Position is continuous (this.pos, in photos). Drags and trackpad swipes move it 1:1, a critically
       damped spring settles it on a photo, and paint() writes every card's transform in the same frame. */
    renderViewer() {
      if (!this.deck) return;
      const focus = this.vFocus;
      const dealAll = this.vDealAll;
      this.vFocus = null;
      this.vDealAll = false;
      const slides = [];
      this.active().forEach((r) => {
        const imgs = this.imagesOf(r.product);
        imgs.forEach((im, i) => slides.push({ r, im, i, n: imgs.length }));
      });
      const key = slides.map((s) => `${s.r.id}:${s.r.product.handle}:${s.i}`).join('|');
      const was = this.slides[this.vi];
      let to = focus ? slides.findIndex((s) => s.r.id === focus) : -1;
      if (to < 0 && was) to = slides.findIndex((s) => s.r.id === was.r.id && s.i === was.i);
      if (to < 0 && was) to = slides.findIndex((s) => s.r.id === was.r.id);
      if (to < 0) to = 0;
      const rebuilt = key !== this.vKey;
      this.slides = slides;
      if (rebuilt) {
        this.vKey = key;
        this.deck.innerHTML = slides.map((s, k) =>
          `<div class="kc-shot" data-shot="${k}"><div class="kc-shot__in"><img alt="${esc(s.im.alt)}" data-src="${s.im.src}" data-srcset="${s.im.srcset || ''}" draggable="false"><span class="kc-shot__tag"></span><span class="kc-shot__q" hidden></span><span class="kc-shot__shade"></span></div></div>`).join('');
        this.shots = [...this.deck.querySelectorAll('.kc-shot')];
        this.shots.forEach((c) => { c._shade = c.querySelector('.kc-shot__shade'); });
      }
      // piece tabs (with a segment per photo) and the fullscreen filmstrip
      const firsts = slides.map((s, k) => ({ s, k })).filter(({ s }) => s.i === 0);
      const tabs = this.querySelector('[data-kc-tabs]');
      if (tabs) tabs.innerHTML = firsts.map(({ s, k }) =>
        `<button type="button" class="kc-tab${this.hotId === s.r.id ? ' is-hot' : ''}" data-kc-tab="${s.r.id}" data-kc-goto="${k}"><span><b>${pad(s.r.n)}</b>${esc(this.slots[s.r.slot].label)}</span><span class="kc-tab__segs" aria-hidden="true">${'<i></i>'.repeat(s.n)}</span></button>`).join('');
      const strip = this.querySelector('[data-kc-strip]');
      if (strip) strip.innerHTML = firsts.map(({ s, k }) =>
        `<div class="kc-strip__group"><span class="kc-strip__n">${esc(this.pieceLabel(s.r))}</span>${slides.slice(k, k + s.n).map((x, j) =>
          `<button type="button" class="kc-strip__t" data-kc-thumb="${k + j}" data-kc-goto="${k + j}" aria-label="${esc(this.slots[s.r.slot].label)} ${pad(s.r.n)}, photo ${j + 1} of ${s.n}"><img src="${x.im.thumb}" alt="" loading="lazy"></button>`).join('')}</div>`).join('');
      this.shots.forEach((c, k) => {
        const s = slides[k];
        c.querySelector('.kc-shot__tag').textContent = this.pieceLabel(s.r);
        const q = c.querySelector('.kc-shot__q');
        q.hidden = s.r.qty < 2;
        q.textContent = `×${s.r.qty}`;
      });
      if (rebuilt) { cancelAnimationFrame(this.raf); this.pos = this.target = to; this.vel = 0; }
      this.measure();
      this.setActive(Math.round(this.target ?? to), { announce: false, force: true });
      // deal the changed piece (or the whole kit on a style switch) onto the board; never on first load
      if (rebuilt && this.ready && (focus || dealAll) && !reduce.matches) {
        this.shots.forEach((c, k) => {
          const d = Math.abs(k - to);
          if (d > 2 || (focus && slides[k].r.id !== focus)) return;
          c.style.setProperty('--deal', `${d * 50}ms`);
          c.classList.add('is-dealt');
          c.addEventListener('animationend', () => c.classList.remove('is-dealt'), { once: true });
        });
      }
    }

    // card size follows the deck; the widest photo still has to fit across a phone
    measure() {
      if (!this.deck || !this.slides.length || !this.shots) return;
      const h = this.deck.clientHeight;
      const w = this.deck.clientWidth;
      if (!h || !w) return;
      const full = this.dlg?.open;
      const widest = Math.max(0.5, ...this.slides.map((s) => s.im.ratio));
      // fullscreen photos take most of a phone's width; inline, the neighbours keep more room to peek
      const cardH = Math.max(120, Math.round(Math.min(h - (full ? 24 : 16), (w * (full ? 0.88 : 0.74)) / widest)));
      // distance between neighbours = finger travel per photo, so a phone swipe doesn't race through the deck
      this.spacing = Math.round(Math.min(cardH * 0.42, w * 0.32));
      this.shots.forEach((c, k) => {
        const s = this.slides[k];
        if (!s) return;
        const cw = Math.round(cardH * s.im.ratio);
        Object.assign(c.style, { width: `${cw}px`, height: `${cardH}px`, marginLeft: `${-cw / 2}px`, marginTop: `${-cardH / 2}px` });
        c._sizes = `${cw}px`;
        const img = c.querySelector('img');
        if (img.getAttribute('srcset')) img.sizes = c._sizes;
      });
      this.paint();
    }

    load(c) {
      const img = c.querySelector('img');
      if (!img || img.getAttribute('src')) return;
      if (img.dataset.srcset) { img.sizes = c._sizes || '400px'; img.srcset = img.dataset.srcset; }
      img.src = img.dataset.src;
    }

    // one frame of the deck: every card's place comes from its distance to this.pos
    paint() {
      if (!this.shots?.length) return;
      const pos = this.pos ?? 0;
      const sp = this.spacing || 120;
      this.shots.forEach((c, k) => {
        const d = k - pos;
        const ad = Math.abs(d);
        if (ad > 3.4) { if (c._vis !== 0) { c.style.visibility = 'hidden'; c._vis = 0; } return; }
        if (c._vis !== 1) { c.style.visibility = ''; c._vis = 1; }
        const a = Math.min(ad, 3);
        c.style.transform = `translate3d(${(d * sp).toFixed(1)}px, ${(a * 6).toFixed(1)}px, 0) rotate(${clamp(d * 2.5, -8, 8).toFixed(2)}deg) scale(${(1 - a * 0.11).toFixed(4)})`;
        const z = String(100 - Math.round(ad * 10));
        if (c._z !== z) { c.style.zIndex = z; c._z = z; }
        c._shade.style.opacity = (a * 0.24).toFixed(3);
        if (ad <= 2.5) this.load(c);
      });
      // the tabs and caption follow the photo nearest the front while a gesture is moving the deck
      if (this.dragging || this.wheeling) this.setActive(Math.round(pos));
    }

    settle() {
      cancelAnimationFrame(this.raf);
      if (reduce.matches) { this.pos = this.target; this.vel = 0; this.paint(); return; }
      let last = performance.now();
      const k = 200;
      const c = 2 * Math.sqrt(k); // critically damped: quick, no wobble
      const tick = (now) => {
        const dt = Math.min(0.032, (now - last) / 1000);
        last = now;
        this.vel += (-k * (this.pos - this.target) - c * this.vel) * dt;
        this.pos += this.vel * dt;
        if (Math.abs(this.pos - this.target) < 0.0005 && Math.abs(this.vel) < 0.002) { this.pos = this.target; this.vel = 0; this.paint(); return; }
        this.paint();
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    go(i, { announce = true, jump = false } = {}) {
      const n = this.slides.length;
      if (!n || !this.deck) return;
      this.target = clamp(i, 0, n - 1);
      this.setActive(this.target, { announce, jump });
      if (jump) { cancelAnimationFrame(this.raf); this.pos = this.target; this.vel = 0; this.paint(); } else this.settle();
    }
    nudge(dir) { this.go(Math.round(this.target ?? this.vi) + dir); }

    // tabs, caption, counter, arrows and filmstrip for photo i
    setActive(i, { announce = true, jump = false, force = false } = {}) {
      const n = this.slides.length;
      if (!n) return;
      i = clamp(i, 0, n - 1);
      if (i === this.vi && !force) return;
      this.vi = i;
      const s = this.slides[i];
      this.shots?.forEach((c, k) => { c.classList.toggle('is-on', k === i); c.setAttribute('aria-hidden', k === i ? 'false' : 'true'); });
      this.querySelectorAll('[data-kc-tab]').forEach((t) => {
        const on = t.dataset.kcTab === s.r.id;
        t.setAttribute('aria-current', on ? 'true' : 'false');
        t.querySelectorAll('i').forEach((seg, j) => { seg.classList.toggle('is-on', on && j === s.i); seg.classList.toggle('is-seen', on && j < s.i); });
      });
      const p = s.r.product;
      const name = this.querySelector('[data-kc-name]');
      if (name) { name.textContent = `${p.name}${p.cut ? ` · ${p.cut}` : ''}`; name.href = p.url; }
      const count = this.querySelector('[data-kc-counter]');
      if (count) count.textContent = `${pad(s.i + 1)}/${pad(s.n)}`;
      this.querySelectorAll('[data-kc-step]').forEach((b) => {
        const end = Number(b.dataset.kcStep) < 0 ? i === 0 : i === n - 1;
        b.setAttribute('aria-disabled', end ? 'true' : 'false');
      });
      const strip = this.querySelector('[data-kc-strip]');
      this.querySelectorAll('[data-kc-thumb]').forEach((t) => {
        const on = Number(t.dataset.kcThumb) === i;
        t.setAttribute('aria-current', on ? 'true' : 'false');
        // keep the current thumb centred in the filmstrip (without scrolling the page)
        if (on && this.dlg?.open && strip) strip.scrollTo({ left: t.offsetLeft - strip.clientWidth / 2 + t.offsetWidth / 2, behavior: reduce.matches || jump ? 'auto' : 'smooth' });
      });
      const live = this.querySelector('[data-kc-live]');
      if (live && announce) live.textContent = `${this.slots[s.r.slot].label} ${pad(s.r.n)}, ${p.name}: photo ${s.i + 1} of ${s.n}`;
    }

    /* Gestures. Touch uses touch events: once a gesture reads sideways, every touchmove calls preventDefault so
       the page can't drift vertically under the swipe (iOS ignores touch-action for diagonal drift); a gesture
       that starts vertical goes back to the page. Mouse and pen use pointer events. Trackpad: see the wheel. */
    initGestures() {
      const el = this.deck;
      const n = () => this.slides.length;
      const band = (p) => { const max = n() - 1; return p < 0 ? p * 0.3 : p > max ? max + (p - max) * 0.3 : p; };
      let x0 = 0, y0 = 0, pos0 = 0;
      let trail = []; // recent {x, t} samples: release speed is measured over the last 100ms
      let state = 'idle'; // idle | pending | dragging | scrolling
      const begin = (x, y) => { x0 = x; y0 = y; trail = []; state = 'pending'; };
      const move = (x, y) => {
        if (state === 'idle' || state === 'scrolling') return false;
        if (state === 'pending') {
          const dx = x - x0, dy = y - y0;
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return false;
          if (Math.abs(dy) > Math.abs(dx)) { state = 'scrolling'; return false; }
          state = 'dragging';
          cancelAnimationFrame(this.raf);
          this.vel = 0;
          pos0 = this.pos;
          x0 = x; // start from here, so the deck doesn't jump by the dead zone
          this.dragging = true;
          this.view.classList.add('is-dragging');
        }
        const now = performance.now();
        trail.push({ x, t: now });
        while (trail.length > 2 && now - trail[0].t > 100) trail.shift();
        this.pos = band(pos0 - (x - x0) / (this.spacing || 120));
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
        return true;
      };
      const end = () => {
        const was = state;
        state = 'idle';
        if (was !== 'dragging') return;
        this.dragging = false;
        this.view.classList.remove('is-dragging');
        this.justDragged = true;
        setTimeout(() => (this.justDragged = false), 80);
        cancelAnimationFrame(this.frame); this.frame = 0;
        const a = trail[0];
        const b = trail[trail.length - 1];
        // px/ms over the last ~100ms; zero if the finger stopped before it lifted
        const v = a && b && b.t - a.t >= 12 && performance.now() - b.t < 90 ? (b.x - a.x) / (b.t - a.t) : 0;
        const perSec = (-v * 1000) / (this.spacing || 120); // photos per second
        // a throw carries on a little, at most two photos past where it was let go
        const here = Math.round(this.pos);
        const t = clamp(clamp(Math.round(this.pos + perSec * 0.14), here - 2, here + 2), 0, n() - 1);
        // hand the throw's speed to the spring only when it agrees with where we're going
        this.vel = Math.sign(perSec) === Math.sign(t - this.pos) ? clamp(perSec, -12, 12) : 0;
        if (t !== Math.round(pos0)) window.SP?.haptic && window.SP.haptic(6);
        this.go(t);
      };
      el.addEventListener('touchstart', (e) => { if (e.touches.length === 1) begin(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
      el.addEventListener('touchmove', (e) => {
        if (state === 'idle' || state === 'scrolling') return;
        const t = e.touches[0];
        if (move(t.clientX, t.clientY) && e.cancelable) e.preventDefault();
      }, { passive: false });
      el.addEventListener('touchend', end);
      el.addEventListener('touchcancel', end);
      let pid = null;
      el.addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch' || e.button !== 0) return; pid = e.pointerId; begin(e.clientX, e.clientY); });
      el.addEventListener('pointermove', (e) => { if (e.pointerId === pid && move(e.clientX, e.clientY) && !el.hasPointerCapture(pid)) el.setPointerCapture(pid); });
      const pend = (e) => { if (e.pointerId !== pid) return; pid = null; end(); };
      el.addEventListener('pointerup', pend);
      el.addEventListener('pointercancel', pend);
      el.addEventListener('dragstart', (e) => e.preventDefault());
      el.addEventListener('click', (e) => { if (this.justDragged) { e.preventDefault(); e.stopPropagation(); } }, true);

      // Trackpad: a sideways swipe moves the deck 1:1 (momentum included) and settles on the nearest photo
      // once it stops. The axis is locked per gesture, so a sideways swipe never scrolls the page and a
      // vertical one is left to the page.
      let axis = null, axisT = 0, sumX = 0, sumY = 0, held = 0, count = 0, raw = 0, idle = 0;
      el.addEventListener('wheel', (e) => {
        const now = performance.now();
        if (now - axisT > 200) { axis = null; sumX = sumY = held = count = 0; } // a new gesture
        axisT = now;
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
        const dx = (e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX) * unit;
        const dy = (e.shiftKey && !e.deltaX ? 0 : e.deltaY) * unit;
        if (!axis) {
          // read the direction from the gesture's opening events (~16px or 3 events), not one noisy event
          sumX += Math.abs(dx); sumY += Math.abs(dy); held += dx; count += 1;
          // while undecided, hold the page still for anything with a real sideways part
          if (sumX + sumY < 16 && count < 3) { if (sumX >= sumY * 0.5) e.preventDefault(); return; }
          axis = sumX > sumY ? 'x' : 'y';
          if (axis === 'y') return;
        }
        if (axis !== 'x') return;
        e.preventDefault();
        if (!this.wheeling) { cancelAnimationFrame(this.raf); this.vel = 0; raw = this.pos; this.wheeling = true; }
        const d = held || dx; // the first sideways event also applies what was held back while deciding
        held = 0;
        raw = clamp(raw + d / (this.spacing || 120), -0.6, n() - 1 + 0.6);
        this.pos = band(raw);
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
        clearTimeout(idle);
        idle = setTimeout(() => { this.wheeling = false; this.go(Math.round(clamp(this.pos, 0, n() - 1))); }, 130);
      }, { passive: false });
    }

    onShot(e) {
      const c = e.target.closest('.kc-shot');
      if (!c) return;
      const k = Number(c.dataset.shot);
      if (k !== this.vi) this.go(k);
      else if (!this.dlg?.open) this.openFull();
    }

    onKey(e) {
      if (!e.target.closest?.('[data-kc-view]')) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.nudge(e.key === 'ArrowRight' ? 1 : -1); }
      else if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); this.go(e.key === 'Home' ? 0 : this.slides.length - 1); }
    }

    // a row's thumbnail: browse that piece in place when the viewer is on screen (desktop), else fullscreen
    look(id) {
      const k = this.slides.findIndex((s) => s.r.id === id);
      if (k < 0) return;
      const box = this.view.getBoundingClientRect();
      const onScreen = box.top > -box.height * 0.4 && box.bottom < innerHeight + box.height * 0.4;
      if (window.matchMedia('(min-width: 990px)').matches && onScreen) this.go(k);
      else { this.go(k, { announce: false }); this.openFull(); }
    }

    openFull() {
      if (!this.dlg || this.dlg.open || !this.view) return;
      this.vReturn = document.activeElement;
      this.dlg.appendChild(this.view);
      this.dlg.showModal();
      document.documentElement.style.overflow = 'hidden';
      // real fullscreen where the browser allows it (not iPhone); the modal dialog covers the screen either way
      const fs = this.dlg.requestFullscreen || this.dlg.webkitRequestFullscreen;
      if (fs) { try { const p = fs.call(this.dlg); if (p?.catch) p.catch(() => {}); } catch (_) {} }
      const btn = this.view.querySelector('[data-kc-full]');
      btn.setAttribute('aria-label', 'Close fullscreen');
      btn.focus({ preventScroll: true });
      this.measure();
      this.go(this.vi, { announce: false, jump: true });
    }

    closeFull() {
      if (!this.dlg?.open || this.closing) return;
      this.closing = true;
      if (document.fullscreenElement === this.dlg) document.exitFullscreen().catch(() => {});
      this.dlg.close();
      this.viewHome.appendChild(this.view);
      document.documentElement.style.overflow = '';
      this.view.querySelector('[data-kc-full]').setAttribute('aria-label', 'View photos fullscreen');
      const back = this.vReturn && document.contains(this.vReturn) && this.vReturn !== document.body ? this.vReturn : this.deck;
      back.focus({ preventScroll: true });
      this.measure();
      this.go(this.vi, { announce: false, jump: true });
      this.closing = false;
    }

    renderTotals() {
      const act = this.active();
      const pieces = act.reduce((a, r) => a + r.qty, 0);
      const total = act.reduce((a, r) => a + (r.variant?.price ?? r.product.price) * r.qty, 0);
      const missing = act.filter((r) => !r.variant).length;
      // the offer: every eligible piece is pct% off once there are at least min of them (per line, like Shopify and Shiprocket)
      const o = this.offer;
      let eligible = 0;
      let saving = 0;
      if (o) {
        act.forEach((r) => { if (r.product.offer) eligible += r.qty; });
        if (eligible >= o.min) act.forEach((r) => { if (r.product.offer) saving += Math.round(((r.variant?.price ?? r.product.price) * r.qty * o.pct) / 100); });
      }
      const count = `${pieces} ${pieces === 1 ? 'piece' : 'pieces'}${saving ? ` · ${o.pct}% off` : ''}${missing ? ` · ${missing} size${missing > 1 ? 's' : ''} to pick` : ''}`;
      const set = (sel, v) => this.querySelectorAll(sel).forEach((el) => { el.textContent = v; });
      set('[data-kc-count]', count);
      set('[data-kc-bar-count]', count);
      set('[data-kc-total]', money(total - saving));
      set('[data-kc-bar-total]', money(total - saving));
      this.querySelectorAll('[data-kc-was]').forEach((el) => { el.hidden = !saving; el.textContent = money(total); });
      const need = o ? o.min - eligible : 0;
      this.querySelectorAll('[data-kc-offer]').forEach((el) => {
        el.hidden = !o || (!saving && (eligible === 0 || need <= 0));
        if (el.hidden) return;
        el.innerHTML = `<span class="badge badge--offer">${esc(o.name)}</span><span>${saving
          ? `${o.pct}% off applied · you save ${money(saving)}`
          : `Add ${need} more piece${need > 1 ? 's' : ''} to get ${o.pct}% off`}</span>`;
      });
      const label = `Add kit to bag · ${pieces}`;
      this.querySelectorAll('[data-kc-add-label]').forEach((el) => { el.textContent = label; });
      this.querySelectorAll('.kc__bar-btn').forEach((el) => { el.textContent = `Add kit · ${pieces}`; });
      this.querySelectorAll('[data-kc-add]').forEach((b) => { b.disabled = pieces === 0; });
    }

    hot(id) {
      this.hotId = id || null;
      this.querySelectorAll('.kc-row.is-hot, .kc-tab.is-hot').forEach((e) => e.classList.remove('is-hot'));
      if (!id) return;
      this.querySelector(`[data-row="${id}"]`)?.classList.add('is-hot');
      this.querySelector(`[data-kc-tab="${id}"]`)?.classList.add('is-hot');
    }

    rerenderRow(r) {
      const el = this.querySelector(`[data-row="${r.id}"]`);
      if (!el) return this.render();
      const tmp = document.createElement('div');
      tmp.innerHTML = this.rowHTML(r).trim();
      el.replaceWith(tmp.firstElementChild);
      this.renumber();
      this.renderViewer();
      this.renderTotals();
    }

    /* ---------- interactions ---------- */
    choose(r, product) {
      const size = r.variant?.size;
      r.product = product;
      r.variant = size ? product.variants.find((v) => v.size === size && v.available) || null : null;
    }

    onClick(e) {
      const t = e.target.closest('button');
      if (!t || !this.contains(t)) return;
      // viewer controls
      if (t.dataset.kcGoto !== undefined) { this.go(Number(t.dataset.kcGoto)); return; }
      if (t.dataset.kcStep) { this.nudge(Number(t.dataset.kcStep)); return; }
      if (t.dataset.kcFull !== undefined) { if (this.dlg?.open) this.closeFull(); else this.openFull(); return; }
      if (t.dataset.kcLook) { this.look(t.dataset.kcLook); return; }
      const r = t.dataset.for ? this.rowById(t.dataset.for) : null;
      const slot = r ? this.slots[r.slot] : null;
      if (t.dataset.kcStyle) {
        if (t.dataset.kcStyle === this.kitStyle) return;
        this.kitStyle = t.dataset.kcStyle;
        // every piece (extras too) moves to the new style, keeping its sleeve and size where it can
        this.rows.forEach((row) => this.choose(row, this.pickFor(this.slots[row.slot], this.kitStyle, row.product)));
        this.vDealAll = true;
        this.render();
        window.SP?.haptic && window.SP.haptic(6);
      } else if (t.dataset.kcCut && r) {
        const next = slot.products.find((p) => p.handle === t.dataset.kcCut);
        if (next) { this.choose(r, next); this.vFocus = r.id; this.rerenderRow(r); }
      } else if (t.dataset.kcSize && r) {
        r.variant = r.product.variants.find((v) => String(v.id) === t.dataset.kcSize) || null;
        // pick once, we match the rest
        if (r.variant) this.active().forEach((o) => {
          if (o !== r && !o.variant) {
            const m = o.product.variants.find((v) => v.size === r.variant.size && v.available);
            if (m) { o.variant = m; this.rerenderRow(o); }
          }
        });
        this.querySelector(`[data-row="${r.id}"]`)?.classList.remove('is-missing');
        this.rerenderRow(r);
        window.SP?.haptic && window.SP.haptic(6);
      } else if (t.dataset.kcQty && r) {
        r.qty = Math.max(1, Math.min(10, r.qty + Number(t.dataset.kcQty)));
        this.rerenderRow(r);
      } else if (t.dataset.kcToggle) {
        const row = this.rowById(t.dataset.kcToggle);
        row.on = !row.on;
        if (row.on) this.vFocus = row.id;
        this.rerenderRow(row);
      } else if (t.dataset.kcRemove) {
        this.rows = this.rows.filter((x) => x.id !== t.dataset.kcRemove);
        this.render();
      } else if (t.dataset.kcMore !== undefined) {
        const si = Number(t.dataset.kcMore);
        const s = this.slots[si];
        const last = [...this.rows].reverse().find((x) => x.slot === si);
        // a sensible second pick: same style, the other cut (e.g. half sleeve for the nets)
        const other = last && s.products.find((p) => p.style === last.product.style && p.handle !== last.product.handle && p.available);
        const row = this.newRow(si, other || last?.product || s.products[0], true);
        if (last?.variant) row.variant = row.product.variants.find((v) => v.size === last.variant.size && v.available) || null;
        row.isNew = true;
        const idx = this.rows.indexOf(last);
        this.rows.splice(idx + 1, 0, row);
        this.vFocus = row.id;
        this.render();
      }
    }

    async addAll() {
      const act = this.active();
      const missing = act.filter((r) => !r.variant);
      if (missing.length) {
        missing.forEach((r) => this.querySelector(`[data-row="${r.id}"]`)?.classList.add('is-missing'));
        this.querySelector(`[data-row="${missing[0].id}"]`)?.scrollIntoView({ block: 'center', behavior: reduce.matches ? 'auto' : 'smooth' });
        window.SP?.toast && window.SP.toast(window.SP_CONFIG?.strings?.selectSize || 'Pick a size for every piece', 'error');
        window.SP?.haptic && window.SP.haptic([10, 40, 10]);
        return;
      }
      const qty = new Map();
      act.forEach((r) => qty.set(r.variant.id, (qty.get(r.variant.id) || 0) + r.qty));
      const items = [...qty].map(([id, quantity]) => ({ id, quantity }));
      const btns = this.querySelectorAll('[data-kc-add]');
      btns.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      try {
        await window.SP.cart.add(items, { silent: true, source: this });
        this.classList.add('is-added');
        window.SP?.haptic && window.SP.haptic(18);
        window.SP?.flyToBag && window.SP.flyToBag(this.deck?.querySelector('.kc-shot.is-on img'));
        setTimeout(() => window.SP?.drawers?.open('CartDrawer'), 850);
        setTimeout(() => this.classList.remove('is-added'), 2400);
      } catch (err) {
        window.SP?.toast && window.SP.toast(err.message || 'Could not add the kit', 'error');
      } finally {
        btns.forEach((b) => { b.disabled = false; b.removeAttribute('aria-busy'); });
      }
    }
  }
  customElements.define('sp-kitcfg', SpKitCfg);
})();
